import { DatePicker as MuiDatePicker } from '@mui/x-date-pickers/DatePicker';
import { useEffect, useState, useRef } from 'react';
import type { DateValidationError } from '@mui/x-date-pickers/models';
import { isValid, parse } from 'date-fns';

export const DEFAULT_DATE_FORMAT = 'yyyy-MM-dd';

// Parse with the display format so date-only strings (yyyy-MM-dd) resolve to local
// midnight; new Date('yyyy-MM-dd') is UTC midnight, which renders as the previous
// day in UTC-negative timezones. Falls back to Date parsing for full ISO timestamps.
const parseDateValue = (value: string, format: string): Date | null => {
  const parsed = parse(value, format, new Date());
  if (isValid(parsed)) return parsed;

  const fallback = new Date(value);
  return isValid(fallback) ? fallback : null;
};

export interface DatePickerProps {
  // Value and onChange
  value?: string | null;
  onChange: (newDate?: string | null) => void;

  // Display
  label?: string;
  format?: string;
  tooltip?: string;
  helperText?: string;
  fullWidth?: boolean;
  size?: 'small' | 'medium';

  // Validation
  minDate?: Date;
  maxDate?: Date;
  disabled?: boolean;
  required?: boolean;

  // Legacy props support (for backward compatibility)
  date?: string | null;
  defaultDate?: string;
  onDayChange?: (event: React.ChangeEvent<{ value: unknown }>) => void;
  pickerType?: string; // ignored - kept for compatibility
}

/**
 * Unified DatePicker Component
 *
 * Consolidates:
 * - DatePicker.tsx (legacy TextField-based)
 * - DatePickerV2.tsx (modern MUI X-based)
 *
 * This component uses MUI X DatePicker with string-based value interface
 * for easier integration with form state and API calls.
 *
 * @example
 * // Modern usage
 * <DatePicker
 *   value={dateString}
 *   onChange={(newDate) => setDateString(newDate)}
 *   label="Select Date"
 * />
 *
 * @example
 * // Legacy compatibility (for migration)
 * <DatePicker
 *   defaultDate="2025-01-01"
 *   onDayChange={(e) => handleChange(e.target.value)}
 * />
 */
const DatePicker = ({
  // Modern API (preferred)
  value,
  onChange,
  label = 'Date',
  format = DEFAULT_DATE_FORMAT,
  tooltip = '',
  helperText,
  fullWidth = false,
  size = 'medium',
  minDate,
  maxDate,
  disabled = false,
  required = false,

  // Legacy API (backward compatibility)
  date,
  defaultDate,
  onDayChange,
}: DatePickerProps) => {
  // Handle both modern and legacy APIs
  const effectiveValue = value ?? date ?? defaultDate ?? '';
  const effectiveOnChange =
    onChange ||
    ((newValue: string | null) => {
      // Legacy onChange adapter
      if (onDayChange) {
        onDayChange({ target: { value: newValue } } as React.ChangeEvent<{ value: unknown }>);
      }
    });

  // Convert string to Date for MUI DatePicker
  const lastValueRef = useRef(effectiveValue);
  const [dateValue, setDateValue] = useState<Date | null>(
    effectiveValue ? parseDateValue(effectiveValue, format) : null,
  );

  // Sync with external value prop changes
  useEffect(() => {
    // Only update if prop actually changed (avoid cascading renders)
    if (effectiveValue === lastValueRef.current) return;

    lastValueRef.current = effectiveValue;

    setDateValue(effectiveValue ? parseDateValue(effectiveValue, format) : null);
  }, [effectiveValue, format]);

  const handleChange = (newValue: Date | null) => {
    setDateValue(newValue);

    // MUI X emits an Invalid Date mid-edit (e.g. an incomplete section or an
    // impossible date like Feb 31). Report it as null — the native date input
    // contract ('' until the content is a complete valid date) — so callers'
    // validation and submit guards treat the field as unset instead of holding
    // a stale previous date. Stringifying it would yield 'NaN-NaN-NaN'.
    // Convert Date back to string (yyyy-MM-dd) using local date parts to avoid
    // UTC conversion timezone issues.
    const stringValue =
      newValue && isValid(newValue)
        ? `${newValue.getFullYear()}-${String(newValue.getMonth() + 1).padStart(2, '0')}-${String(newValue.getDate()).padStart(2, '0')}`
        : null;

    effectiveOnChange(stringValue);
  };

  const handleError = (error: DateValidationError) => {
    // Suppress MUI validation errors to prevent orange border
    // This allows the DatePicker to accept partial/incomplete dates during typing
    // without showing error states
    return;
  };

  return (
    <MuiDatePicker
      label={label}
      format={format}
      value={dateValue}
      onChange={handleChange}
      onError={handleError}
      disabled={disabled}
      minDate={minDate}
      maxDate={maxDate}
      slotProps={{
        textField: {
          slotProps: {
            inputLabel: {
              shrink: true,
            },
          },
          title: tooltip, // Native HTML title attribute for tooltip
          helperText,
          fullWidth,
          size,
          required,
        },
      }}
    />
  );
};

export default DatePicker;
